import React, { useRef, useImperativeHandle, forwardRef, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Select, MenuItem, Tooltip } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';

const PRIMARY = '#f25d27';

const BLOCK_OPTIONS = [
  { label: 'Texto', value: 'p' },
  { label: 'Título 1', value: 'h1' },
  { label: 'Título 2', value: 'h2' },
  { label: 'Título 3', value: 'h3' },
  { label: 'Título 4', value: 'h4' },
  { label: 'Título 5', value: 'h5' },
  { label: 'Título 6', value: 'h6' },
];

const COLORS = [
  { label: 'Padrão', value: '' },
  { label: 'Preto', value: '#000000' },
  { label: 'Vermelho', value: '#c0392b' },
  { label: 'Azul', value: '#1565c0' },
  { label: 'Verde', value: '#2e7d32' },
  { label: 'Laranja', value: '#e65100' },
  { label: 'Cinza', value: '#616161' },
];

const BLOCK_NODE_TYPES = new Set([
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'blockquote',
  'codeBlock',
  'horizontalRule',
]);

/**
 * Alguns navegadores deixam texto digitado diretamente como filho da div
 * editável (sem envolvê-lo em <p>/<div>). Essa função garante que todo
 * conteúdo de nível raiz seja um nó de bloco, envolvendo trechos inline
 * soltos (texto, hardBreak) em parágrafos.
 * @param {import('../../utils/adf').TiptapJSON[]} nodes
 * @returns {import('../../utils/adf').TiptapJSON[]}
 */
function wrapStrayInlineNodes(nodes) {
  const result = [];
  let buffer = [];

  function flushBuffer() {
    if (buffer.length) {
      result.push({ type: 'paragraph', content: buffer });
      buffer = [];
    }
  }

  for (const node of nodes) {
    if (!node) continue;
    if (BLOCK_NODE_TYPES.has(node.type)) {
      flushBuffer();
      result.push(node);
    } else {
      buffer.push(node);
    }
  }
  flushBuffer();

  return result;
}

/**
 * Converte o conteúdo do contenteditable para um JSON compatível com TipTap.
 * @param {HTMLElement} el
 * @returns {import('../../utils/adf').TiptapJSON}
 */
function domToTiptapJson(el) {
  const content = [];

  for (const child of Array.from(el.childNodes)) {
    const node = parseNode(child);
    if (node) content.push(node);
  }

  return { type: 'doc', content: wrapStrayInlineNodes(content) };
}

/**
 * @param {Node} node
 * @returns {import('../../utils/adf').TiptapJSON | null}
 */
function parseNode(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent || '';
    if (!text) return null;
    return { type: 'text', text };
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const el = /** @type {HTMLElement} */ (node);
  const tag = el.tagName.toLowerCase();

  if (tag === 'br') return { type: 'hardBreak' };

  if (/^h[1-6]$/.test(tag)) {
    const level = parseInt(tag[1], 10);
    const children = parseInlineChildren(el);
    if (!children.length) return null;
    return { type: 'heading', attrs: { level }, content: children };
  }

  if (tag === 'p' || tag === 'div') {
    const children = parseInlineChildren(el);
    if (!children.length) return { type: 'paragraph', content: [{ type: 'text', text: ' ' }] };
    return { type: 'paragraph', content: children };
  }

  if (tag === 'ul') {
    const items = Array.from(el.querySelectorAll(':scope > li')).map((li) => {
      const children = parseInlineChildren(/** @type {HTMLElement} */ (li));
      return { type: 'listItem', content: [{ type: 'paragraph', content: children.length ? children : [{ type: 'text', text: ' ' }] }] };
    });
    return { type: 'bulletList', content: items };
  }

  if (tag === 'ol') {
    const items = Array.from(el.querySelectorAll(':scope > li')).map((li) => {
      const children = parseInlineChildren(/** @type {HTMLElement} */ (li));
      return { type: 'listItem', content: [{ type: 'paragraph', content: children.length ? children : [{ type: 'text', text: ' ' }] }] };
    });
    return { type: 'orderedList', content: items };
  }

  if (tag === 'blockquote') {
    const children = parseInlineChildren(el);
    return { type: 'blockquote', content: [{ type: 'paragraph', content: children }] };
  }

  if (tag === 'pre' || tag === 'code') {
    return { type: 'codeBlock', content: [{ type: 'text', text: el.textContent || '' }] };
  }

  // Fallback: tratar como parágrafo
  const children = parseInlineChildren(el);
  if (!children.length) return null;
  return { type: 'paragraph', content: children };
}

/**
 * @param {HTMLElement} el
 * @returns {import('../../utils/adf').TiptapJSON[]}
 */
function parseInlineChildren(el) {
  const result = [];

  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent || '';
      if (text) result.push({ type: 'text', text });
      continue;
    }

    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const childEl = /** @type {HTMLElement} */ (child);
    const tag = childEl.tagName.toLowerCase();

    if (tag === 'br') {
      result.push({ type: 'hardBreak' });
      continue;
    }

    const marks = [];
    let innerTag = tag;

    if (innerTag === 'b' || innerTag === 'strong') marks.push({ type: 'bold' });
    if (innerTag === 'i' || innerTag === 'em') marks.push({ type: 'italic' });
    if (innerTag === 'u') marks.push({ type: 'underline' });
    if (innerTag === 'code') marks.push({ type: 'code' });

    // Detectar cor via style
    const color = childEl.style?.color;
    if (color) marks.push({ type: 'textStyle', attrs: { color } });

    const innerChildren = parseInlineChildren(childEl);
    for (const ic of innerChildren) {
      if (ic.type === 'text' && marks.length) {
        result.push({ type: 'text', text: ic.text || '', marks: [...marks] });
      } else {
        result.push(ic);
      }
    }
  }

  return result;
}

/**
 * Editor rich text usando contenteditable + execCommand.
 *
 * Exporta via ref:
 *   - getJson(): retorna JSON tiptap
 *   - clear(): limpa conteúdo
 *   - isEmpty(): true se vazio
 *   - focus(): foca o editor
 */
const RichTextEditor = forwardRef(function RichTextEditor(
  {
    label,
    placeholder = 'Escreva uma mensagem...',
    minHeight = 120,
    maxHeight = 400,
    onChange,
    onCtrlEnter,
  },
  ref,
) {
  const editorRef = useRef(null);
  const [blockType, setBlockType] = useState('p');
  const [textColor, setTextColor] = useState('');

  useImperativeHandle(ref, () => ({
    getJson: () => {
      if (!editorRef.current) return { type: 'doc', content: [] };
      return domToTiptapJson(editorRef.current);
    },
    clear: () => {
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
        triggerChange();
      }
    },
    isEmpty: () => {
      if (!editorRef.current) return true;
      return (editorRef.current.textContent || '').trim() === '';
    },
    focus: () => {
      editorRef.current?.focus();
    },
  }));

  function triggerChange() {
    if (onChange && editorRef.current) {
      onChange(domToTiptapJson(editorRef.current));
    }
  }

  function execCmd(cmd, value = null) {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value);
    triggerChange();
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      onCtrlEnter?.();
    }
  }

  function handleInput() {
    triggerChange();
  }

  function applyBlock(value) {
    setBlockType(value);
    editorRef.current?.focus();
    document.execCommand('formatBlock', false, `<${value}>`);
    triggerChange();
  }

  function applyColor(color) {
    setTextColor(color);
    if (color) {
      execCmd('foreColor', color);
    }
  }

  const toolbarSx = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.5,
    alignItems: 'center',
    p: '6px 10px',
    borderBottom: '1px solid #e0e0e0',
    background: '#fafafa',
  };

  const toggleBtnSx = {
    border: 'none !important',
    borderRadius: '4px !important',
    padding: '3px 6px',
    '&.Mui-selected': { background: '#f0f0f0', color: PRIMARY },
    '&:hover': { background: '#f0f0f0' },
    minWidth: 32,
  };

  return (
    <Box
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'hidden',
        '&:focus-within': { borderColor: PRIMARY },
      }}
    >
      {label && (
        <Box sx={{ px: 1.5, pt: 1, pb: 0 }}>
          <Box component="span" sx={{ fontSize: '0.75rem', color: '#666' }}>
            {label}
          </Box>
        </Box>
      )}

      {/* Toolbar */}
      <Box sx={toolbarSx}>
        <Select
          size="small"
          value={blockType}
          onChange={(e) => applyBlock(e.target.value)}
          sx={{
            fontSize: '0.78rem',
            height: 28,
            minWidth: 100,
            '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #ddd' },
          }}
        >
          {BLOCK_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.78rem' }}>
              {o.label}
            </MenuItem>
          ))}
        </Select>

        <Select
          size="small"
          value={textColor}
          displayEmpty
          onChange={(e) => applyColor(e.target.value)}
          renderValue={(v) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12, height: 12, borderRadius: '50%',
                  background: v || '#333',
                  border: '1px solid #ddd',
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: '0.75rem' }}>Cor</span>
            </Box>
          )}
          sx={{
            fontSize: '0.78rem',
            height: 28,
            minWidth: 72,
            '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #ddd' },
          }}
        >
          {COLORS.map((c) => (
            <MenuItem key={c.value} value={c.value} sx={{ fontSize: '0.78rem', gap: 1 }}>
              <Box
                sx={{
                  width: 14, height: 14, borderRadius: '50%',
                  background: c.value || '#333',
                  border: '1px solid #ccc',
                }}
              />
              {c.label}
            </MenuItem>
          ))}
        </Select>

        <ToggleButtonGroup size="small" aria-label="formatação inline">
          <Tooltip title="Negrito (Ctrl+B)">
            <ToggleButton value="bold" sx={toggleBtnSx} onClick={() => execCmd('bold')}>
              <FormatBoldIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Itálico (Ctrl+I)">
            <ToggleButton value="italic" sx={toggleBtnSx} onClick={() => execCmd('italic')}>
              <FormatItalicIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Sublinhado (Ctrl+U)">
            <ToggleButton value="underline" sx={toggleBtnSx} onClick={() => execCmd('underline')}>
              <FormatUnderlinedIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>

        <ToggleButtonGroup size="small" aria-label="listas">
          <Tooltip title="Lista com marcadores">
            <ToggleButton
              value="ul"
              sx={toggleBtnSx}
              onClick={() => execCmd('insertUnorderedList')}
            >
              <FormatListBulletedIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Lista numerada">
            <ToggleButton
              value="ol"
              sx={toggleBtnSx}
              onClick={() => execCmd('insertOrderedList')}
            >
              <FormatListNumberedIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Box>

      {/* Área de edição */}
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
        sx={{
          minHeight,
          maxHeight,
          overflowY: 'auto',
          p: '10px 14px',
          outline: 'none',
          fontSize: '0.9rem',
          lineHeight: 1.6,
          color: '#333',
          '&:empty::before': {
            content: 'attr(data-placeholder)',
            color: '#aaa',
            pointerEvents: 'none',
          },
          '& p, & div': { margin: '0 0 4px' },
          '& h1': { fontSize: '1.4rem', fontWeight: 700, margin: '4px 0' },
          '& h2': { fontSize: '1.25rem', fontWeight: 700, margin: '4px 0' },
          '& h3': { fontSize: '1.1rem', fontWeight: 600, margin: '4px 0' },
          '& h4, & h5, & h6': { fontWeight: 600, margin: '4px 0' },
          '& ul, & ol': { paddingLeft: 20, margin: '4px 0' },
          '& blockquote': {
            borderLeft: `3px solid ${PRIMARY}`,
            paddingLeft: 10,
            margin: '4px 0',
            fontStyle: 'italic',
          },
          '& pre, & code': {
            background: '#f4f4f4',
            borderRadius: 3,
            padding: '2px 4px',
            fontFamily: 'monospace',
          },
        }}
      />
    </Box>
  );
});

export default RichTextEditor;
